/**
 * App context: who am I, which team, the local db, the sync engine and the
 * repos. Everything below the auth/onboarding gate can assume `team` is set.
 */
import {
  Autosaver,
  CoachDb,
  FakeRemote,
  SupabaseRemote,
  SyncEngine,
  TeamRepo,
  getDb,
  kvGet,
  kvSet,
  requestPersistentStorage,
  type AttendanceRow,
  type EvalPlayerRow,
  type EvalScoreRow,
  type LibraryItemRow,
  type LineupRow,
  type PlanFolderRow,
  type PlanRow,
  type PlayerRow,
  type RemoteApi,
  type Role,
  type SyncStatus,
  type TeamSettings,
} from '@coach/core';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HAS_CLOUD, supabase } from './supabase';

export interface TeamInfo {
  id: string;
  name: string;
  season: string;
  role: Role;
}

export interface Repos {
  plans: TeamRepo<PlanRow>;
  folders: TeamRepo<PlanFolderRow>;
  library: TeamRepo<LibraryItemRow>;
  players: TeamRepo<PlayerRow>;
  attendance: TeamRepo<AttendanceRow>;
  evalPlayers: TeamRepo<EvalPlayerRow>;
  evalScores: TeamRepo<EvalScoreRow>;
  lineups: TeamRepo<LineupRow>;
}

export interface AppState {
  mode: 'cloud' | 'local';
  userId: string;
  userName: string;
  email: string;
  team: TeamInfo;
  isHead: boolean;
  db: CoachDb;
  engine: SyncEngine;
  repos: Repos;
  settings: TeamSettings;
  saveSettings: (patch: Partial<TeamSettings>) => Promise<void>;
  sync: () => Promise<void>;
  syncStatus: SyncStatus;
  signOut: () => Promise<void>;
  switchTeam: (teamId: string) => Promise<void>;
}

const Ctx = createContext<AppState | null>(null);
export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp outside AppProvider');
  return v;
}

export const LOCAL_USER = '00000000-0000-4000-8000-000000000001';
export const LOCAL_TEAM = '00000000-0000-4000-8000-000000000002';

// Singletons that live for the page lifetime.
let _remote: RemoteApi | null = null;
function remote(): RemoteApi {
  if (!_remote) _remote = supabase ? new SupabaseRemote(supabase) : new FakeRemote();
  return _remote;
}
let _engine: SyncEngine | null = null;
function engine(onAuth: () => void): SyncEngine {
  if (!_engine) _engine = new SyncEngine({ db: getDb(), remote: remote(), onAuthRequired: onAuth, log: (m, x) => console.warn('[sync]', m, x) });
  return _engine;
}

export interface Identity {
  userId: string;
  userName: string;
  email: string;
  team: TeamInfo;
}

export function AppProvider({ identity, onAuthRequired, onSignOut, onSwitchTeam, children }: {
  identity: Identity;
  onAuthRequired: () => void;
  onSignOut: () => Promise<void>;
  onSwitchTeam: (id: string) => Promise<void>;
  children: ReactNode;
}) {
  const db = getDb();
  const eng = useMemo(() => engine(onAuthRequired), [onAuthRequired]);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(eng.getStatus());
  const [settings, setSettings] = useState<TeamSettings>({});
  const teamId = identity.team.id;

  const repos = useMemo<Repos>(() => {
    const ctx = { db, engine: eng, teamId, userId: identity.userId };
    return {
      plans: new TeamRepo<PlanRow>(ctx, 'plans'),
      folders: new TeamRepo<PlanFolderRow>(ctx, 'plan_folders'),
      library: new TeamRepo<LibraryItemRow>(ctx, 'library_items'),
      players: new TeamRepo<PlayerRow>(ctx, 'players'),
      attendance: new TeamRepo<AttendanceRow>(ctx, 'attendance'),
      evalPlayers: new TeamRepo<EvalPlayerRow>(ctx, 'eval_players'),
      evalScores: new TeamRepo<EvalScoreRow>(ctx, 'eval_scores'),
      lineups: new TeamRepo<LineupRow>(ctx, 'lineups'),
    };
  }, [db, eng, teamId, identity.userId]);

  const sync = useCallback(() => eng.sync(teamId), [eng, teamId]);

  // ── settings: cached in kv, source of truth is teams.settings in the cloud ──
  const settingsKey = `settings:${teamId}`;
  useEffect(() => {
    let alive = true;
    (async () => {
      const cached = (await kvGet<TeamSettings>(settingsKey)) ?? {};
      if (alive) setSettings(cached);
      if (supabase) {
        const { data } = await supabase.from('teams').select('settings').eq('id', teamId).maybeSingle();
        if (alive && data?.settings) {
          setSettings(data.settings as TeamSettings);
          await kvSet(settingsKey, data.settings);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [teamId, settingsKey]);

  const saveSettings = useCallback(
    async (patch: Partial<TeamSettings>) => {
      const next = { ...settings, ...patch };
      setSettings(next);
      await kvSet(settingsKey, next);
      if (supabase) {
        const { error } = await supabase.from('teams').update({ settings: next }).eq('id', teamId);
        if (error) console.warn('[settings] not saved to cloud', error.message);
      }
    },
    [settings, settingsKey, teamId],
  );

  // ── sync triggers: foreground, network regain, every 30 s, status feed ──
  useEffect(() => eng.subscribe(setSyncStatus), [eng]);
  useEffect(() => {
    void requestPersistentStorage();
    void sync();
    const iv = setInterval(() => void sync(), 30_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') void sync();
    };
    const onOnline = () => {
      eng.setOnline(true);
      void sync();
    };
    const onOffline = () => eng.setOnline(false);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [eng, sync]);

  const value: AppState = {
    mode: HAS_CLOUD ? 'cloud' : 'local',
    userId: identity.userId,
    userName: identity.userName,
    email: identity.email,
    team: identity.team,
    isHead: identity.team.role === 'head',
    db,
    engine: eng,
    repos,
    settings,
    saveSettings,
    sync,
    syncStatus,
    signOut: onSignOut,
    switchTeam: onSwitchTeam,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Autosave hook: debounced save with flush on pagehide/hidden/app-inactive. */
export function useAutosaver(delay = 900): Autosaver {
  const ref = useRef<Autosaver | null>(null);
  if (!ref.current) ref.current = new Autosaver(delay);
  useEffect(() => {
    const a = ref.current!;
    const flush = () => void a.flush();
    const onVis = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVis);
    let remove: (() => void) | null = null;
    void import('@capacitor/app')
      .then(({ App }) => App.addListener('appStateChange', (s) => { if (!s.isActive) flush(); }))
      .then((h) => { remove = () => void h.remove(); })
      .catch(() => {});
    return () => {
      flush();
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVis);
      remove?.();
    };
  }, []);
  return ref.current;
}
